import { Controller } from "@hotwired/stimulus"
import { show_error, show_success, check_auth_status, htmlEscape } from "lib/helpers"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { initRemoveItemsHandler, checkboxesGroupController } from "lib/chat_bulk_actions"

// chat--history on `app/views/chat/dialogs/index.haml` (#history_dialogs).
//
// Values (from page_data_tag):
//   users-service-root    – base path for user edit links
//   remove-dialogs-path   – DELETE endpoint for bulk dialog removal
//   chat-list-dialogs-path – POST endpoint for dialogs DataTable AJAX
//   chat-dialogs-path     – base path for dialog edit links
//   chat-messages-path    – base path for messages DataTable AJAX
//   dialogs-sort-column   – default sort column index
//   dialogs-search-changes-enabled – boolean controlling search placeholder
//   export-enabled        – whether export button is shown
//   reader                – "true"/"false" for read-only mode
export default class extends Controller {
  static targets = ["newDialogModal"]

  static values = {
    usersServiceRoot: String,
    removeDialogsPath: String,
    chatListDialogsPath: String,
    chatDialogsPath: String,
    chatMessagesPath: String,
    dialogsSortColumn: Number,
    dialogsSearchChangesEnabled: { type: Boolean, default: false },
    exportEnabled: { type: Boolean, default: false },
    reader: { type: Boolean, default: true },
  }

  connect() {
    if (!document.getElementById("dialogs_list")) return

    this.dialogTable = null
    this.messageTable = null

    this.#initDialogTable()
    this.#initChatCheckboxes()
    checkboxesGroupController("dialogs", "chatHistory")
    checkboxesGroupController("messages", "chatHistory")

    if (this.hasNewDialogModalTarget) {
      $(this.newDialogModalTarget).on("hidden.bs.modal.chatHistory", () => {
        const form = this.newDialogModalTarget.querySelector("#dialog_create_form")
        if (form) form.reset()
      })
    }

    this.#revealWorkspace()
  }

  disconnect() {
    if (this.hasNewDialogModalTarget) {
      $(this.newDialogModalTarget).off(".chatHistory")
    }

    $("#dialogs_list_filter input").off(".chatHistory")
    $("#dialogs_list_wrapper").off(".chatHistory")
    $("#check-all-dialogs").off(".chatHistory")
    $("[id^='check-all-']").off(".chatHistory")
    $("tbody input[type='checkbox']").off(".chatHistory")
    $(document).off(".chatHistory")
    $("#dialog-messages").off(".chatHistory")
    $("#messages_list_filter input").off(".chatHistory")
    $("a[name^='dialog_id']").off(".chatHistory")
    $("div[name^='message_id']").off(".chatHistory")
    $("div[name^='ids']").off(".chatHistory")

    this.#destroyPowerTips()

    if (this.dialogTable) {
      this.dialogTable = destroyTurboDataTable(this.dialogTable)
    }
    if (this.messageTable) {
      this.messageTable = destroyTurboDataTable(this.messageTable)
    }

    $("#dialog-messages").modal("hide")
  }

  newDialog() {
    $("#new-dialog-form").modal("show")
    const ALLNUM_ALLPUNCT = /^[-A-Za-z0-9\u00BF-\u1FFF\u2C00-\uD7FF\]\[!"#$%&'()*+,./:;<=>?@\\^_`{|}~\s]+$/
    const ALLNUM = /^([0-9]+[0-9,]+[0-9]+|\d+)$/
    const radioBtn = $('#dialog_create_form input[type="radio"]')
    const occIds = $("#chat_dialog_occupants_ids")
    occIds.removeAttr("disabled")

    radioBtn.change(function () {
      if ($(this).val() == 1) {
        occIds.val("")
        occIds.attr("disabled", "disabled")
        occIds.removeClass("error")
        $("#chat_dialog_occupants_ids + label.error").remove()
      } else {
        occIds.removeAttr("disabled")
      }
    })

    this.#ensureRegexValidator()

    $("#dialog_create_form").validate({
      focusInvalid: false,
      onfocusout: false,
      rules: {
        "chat_dialog[name]": { required: true, regex: ALLNUM_ALLPUNCT, maxlength: 200 },
        "chat_dialog[occupants_ids]": {
          required: () => radioBtn.filter(":checked").val() == 2,
          regex: ALLNUM,
        },
      },
      messages: {
        "chat_dialog[name]": { required: "Can't be blank" },
        "chat_dialog[occupants_ids]": { required: "Can't be blank" },
      },
    })

    this.#submitDialogCreateForm()
  }

  exportHistory() {
    const path = this.chatMessagesPathValue + "/export_history"
    const groupName = $("input#check-all-dialogs").val()
    const checkboxes = $(`input[name^='${groupName}']:checked`)
    const available = $(`input[name^='${groupName}']`)

    if (!available.length) return false
    if (!checkboxes.length) {
      alert("Please select at least one item")
      return false
    }
    if (checkboxes.length > 1 && !confirm("Are you sure? This can take a long time.")) return false

    const ids = $.makeArray(checkboxes.map(function () { return $(this).val() })).toString()

    $("#get_history_iframe").remove()
    $("body").append(`<iframe id="get_history_iframe" src="${path}?item_ids=${ids}" style="display: none;"></iframe>`)

    $("#get_history_iframe").on("load", () => {
      const iframeBodyText = $("#get_history_iframe").contents().find("body").text()
      if (!iframeBodyText) return
      try {
        const response = jQuery.parseJSON(iframeBodyText)
        if (response.error === true) show_error(response.message)
      } catch {
        show_error($.trim(iframeBodyText) || "Unexpected response while exporting history.")
      }
    })
  }

  showMessages(event) {
    event.preventDefault()
    const dialogId = event.currentTarget.dataset.dialogId
    if (!dialogId) return
    const path = this.chatMessagesPathValue + "/" + dialogId

    $("#dialog-messages").modal("show")
    $("#dialog-messages").on("hidden.bs.modal.chatHistory", () => {
      $("#dateSentFilter .date_range_filter").datepicker("destroy")
      $("#searchInput input").val("")
    })

    $("#dialog-messages").off("shown.bs.modal.chatHistory")
    $("#dialog-messages").on("shown.bs.modal.chatHistory", () => {
      this._currentDialogPath = path
      this.#initMessageTable(path)
    })
  }

  // -- private --

  #ensureRegexValidator() {
    if (!$.validator || $.validator.methods.regex) return

    $.validator.addMethod(
      "regex",
      function (value, element, regexp) {
        return this.optional(element) || regexp.test(value)
      },
      "Please check your input.",
    )
  }

  #revealWorkspace() {
    $("#loading").hide()
    $(this.element).removeClass("tmp-hide").show()
  }

  #initChatCheckboxes() {
    $("#dialogs_list_wrapper").on("change.chatHistory", ".table-checkbox-chat-history", function () {
      const isChecked = $(this).prop("checked")
      $(this).closest("tr").toggleClass("selected-row", isChecked)
      const allChecked = $(".table-checkbox-chat-history:checked").length === $(".table-checkbox-chat-history").length
      $("#check-all-dialogs").prop("checked", allChecked)
    })

    $("#check-all-dialogs").on("change.chatHistory", function () {
      const isChecked = $(this).prop("checked")
      $(".table-checkbox-chat-history").prop("checked", isChecked)
      $(".table-checkbox-chat-history").closest("tr").toggleClass("selected-row", isChecked)
    })
  }

  #isSafari() {
    const safari = navigator.userAgent.match(/Version\/[\d.]+.*Safari/)
    if (!safari) return false
    const ver = safari[0].match(/\d{1,}/)
    return ver && ver[0] <= 9
  }

  #initDialogTable() {
    if (!document.getElementById("dialogs_list")) return

    this.dialogTable = createTurboDataTable("#dialogs_list", {
      dom: 'lr<"head-toolbar">ftip<"footer-toolbar">',
      pagingType: "full_numbers",
      processing: true,
      serverSide: true,
      scrollX: true,
      order: [this.dialogsSortColumnValue, "desc"],
      language: {
        searchPlaceholder: this.dialogsSearchChangesEnabledValue
          ? "by ID, Occupants IDs"
          : "by ID, Name, Last message, Last message user ID, Occupants IDs",
      },
      ajax: {
        url: this.chatListDialogsPathValue,
        type: "POST",
      },
      initComplete: () => {
        $("#loading").hide()
        $("#history_dialogs").show()
        if (localStorage.getItem("show-success") !== "false") {
          show_success(localStorage.getItem("show-success"), $("#update-result"))
          localStorage.setItem("show-success", "false")
        }
        if (!this.#isSafari()) this.#initHoverForDialogId()
        this.#initHoverForIds()
        this.#redrawTable()
      },
      drawCallback: () => {
        if (!this.#isSafari()) this.#initHoverForDialogId()
        this.#initHoverForIds()
      },
      footerCallback: () => {
        $("#check-all-dialogs").attr("checked", false)
      },
      columnDefs: [
        {
          targets: 0,
          createdCell: (cell, cellData) => {
            $(cell).html(
              `<input type="checkbox" class="table-checkbox-chat-history" id="check--history${cellData}" name="item_ids[]" value="${cellData}"/> <label for="check--history${cellData}"><label/>`,
            )
          },
        },
        {
          targets: 1,
          createdCell: (cell, cellData) => {
            const id = htmlEscape(cellData).substring(0, 8) + "..."
            $(cell).html(`<a href="${this.chatDialogsPathValue}/${cellData}/edit" name="dialog_id_${cellData}"><span>${id}</span></a>`)
          },
        },
        {
          targets: 3,
          createdCell: (cell, cellData) => {
            const escaped = htmlEscape(cellData)
            const cropped = escaped.length > 20 ? escaped.substring(0, 20) + "..." : escaped
            $(cell).html(cropped)
          },
        },
        {
          targets: 6,
          createdCell: (cell, cellData) => {
            if (document.getElementById("last-mes-not-allowed")) {
              $(cell).html("").css("display", "none")
            } else {
              $(cell).html(htmlEscape(cellData))
            }
          },
        },
        {
          targets: 7,
          createdCell: (cell, cellData) => {
            if (cellData == null) {
              $(cell).html("null")
            } else {
              $(cell).html(
                `<a href="${this.usersServiceRootValue}/edit/${cellData}" class="${cellData} fancybox.ajax"><span>${htmlEscape(cellData)}</span></a>`,
              )
            }
          },
        },
        {
          targets: 8,
          createdCell: (cell, cellData) => {
            $(cell).html(cellData ? moment(cellData).format() : "")
          },
        },
        {
          targets: 9,
          createdCell: (cell, cellData) => {
            if (document.getElementById("history-not-allowed")) {
              $(cell).html("").css("display", "none")
            } else {
              $(cell).html(
                `<a href="#" data-dialog-id="${cellData}" data-action="click->chat--history#showMessages"><span>view</span></a>`,
              )
            }
          },
        },
        {
          targets: 10,
          createdCell: (cell, cellData) => {
            $(cell).html(cellData ? moment(cellData).format() : "")
          },
        },
        {
          targets: [0, 3, 6, 7, 9],
          sortable: false,
        },
      ],
    })

    const newDialogBtn = $(".add-new-dialog")
    const headToolbar = $("#dialogs_list_wrapper div.head-toolbar")
    headToolbar.append(newDialogBtn.html())
    headToolbar.addClass("pull-right")
    newDialogBtn.remove()

    let footerText = '<br><br><a href="#" class="remove dialogs-remove">Remove selected</a>'
    if (this.exportEnabledValue) {
      footerText += '&nbsp;&nbsp;<a href="#" data-action="click->chat--history#exportHistory">Export history</a>'
    }
    $(".footer-toolbar").html(footerText)
    initRemoveItemsHandler("dialogs", this.dialogTable, "chatHistory", this.removeDialogsPathValue)
  }

  #redrawTable() {
    $(".dataTables").css("width", "100%")
    this.dialogTable.draw(false)
  }

  #submitDialogCreateForm() {
    $("#dialog_create_form").each((_, el) => {
      $(el).ajaxForm({
        dataType: "json",
        beforeSubmit: () => {
          $("#dialogs_list_processing").css("visibility", "visible")
        },
        success: (response) => {
          if (response.success) {
            show_success(response.message, $(el).parent())
            this.dialogTable.draw(false)
          } else {
            $("#dialog_create_form").trigger("reset")
            show_error(response.message, $("#dialog_create_form").parent())
          }
          $("#new-dialog-form").modal("hide")
        },
        error: (response) => {
          $("#new-dialog-form").parent().show()
          show_error(response.message)
          check_auth_status(response.status)
        },
      })
    })
  }

  #initMessageTable(path) {
    if (!document.getElementById("messages_list")) return

    this.messageTable = createTurboDataTable("#messages_list", {
      dom: 'flrt<"toolbar"><"messages-list-footer"ip>',
      pagingType: "full_numbers",
      destroy: true,
      processing: true,
      serverSide: true,
      scrollX: true,
      scrollCollapse: true,
      order: [6, "asc"],
      language: {
        searchPlaceholder: "search by Message field",
        infoFiltered: "",
      },
      ajax: { url: path },
      drawCallback: () => this.#initHoverForMessage(),
      initComplete: () => {
        $("#loading").hide()
        this.#initHoverForMessage()
      },
      footerCallback: () => {
        $("#check-all-messages").attr("checked", false)
      },
      columnDefs: [
        {
          targets: 0,
          createdCell: (cell, cellData) => {
            $(cell).html(
              `<input type="checkbox" class="table-checkbox-chat-message-history" id="check--message-history${cellData}" name="item_ids[]" value="${cellData}"/> <label for="check--message-history${cellData}"><label/>`,
            )
          },
        },
        {
          targets: 1,
          createdCell: (cell, cellData) => {
            let msg = htmlEscape(cellData)
            if (msg.length > 14) msg = msg.substring(0, 14) + "..."
            const time = new Date().getTime().toString()
            $(cell).html(
              `<div style="color: #206daa; cursor: pointer;" name="message_id_${time}" val="${htmlEscape(cellData)}">${msg}</div>`,
            )
          },
        },
        {
          targets: 2,
          createdCell: (cell, cellData) => {
            $(cell).html(
              `<a href="${this.usersServiceRootValue}/edit/${cellData}" class="${cellData}"><span>${htmlEscape(cellData)}</span></a>`,
            )
          },
        },
        {
          targets: 3,
          createdCell: (cell, cellData) => {
            if (cellData == null) return "null"
            $(cell).html(
              `<a href="${this.usersServiceRootValue}/edit/${cellData}" class="${cellData}"><span>${htmlEscape(cellData)}</span></a>`,
            )
          },
        },
        {
          targets: 6,
          createdCell: (cell, cellData) => {
            $(cell).html(`<span class="chat-sender-${cellData}">${moment(cellData).format()}</span>`)
          },
        },
        {
          targets: [0, 1, 2, 3, 4, 5, 6],
          sortable: false,
        },
      ],
    })

    const searchBox = $("#messages_list_filter input")
    const msgTable = this.messageTable
    searchBox.off(".chatHistory").on("input.chatHistory", function () {
      if (this.value.length >= 3) msgTable.search(this.value).draw()
      if (this.value === "") msgTable.search("").draw()
    })

    yadcf.init(this.messageTable, [
      {
        column_number: 6,
        filter_type: "range_date",
        date_format: "dd-mm-yy",
        filter_container_id: "dateSentFilter",
      },
    ])

    if (!this.readerValue) {
      $("#messages_list_wrapper div.toolbar").not(".rich").html('<br/><a href="#" class="remove messages-remove">Remove selected</a>')
      initRemoveItemsHandler("messages", this.messageTable, "chatHistory", () => this._currentDialogPath + "/remove")
    }
  }

  #destroyPowerTips() {
    const selectors = ['a[name^="dialog_id"]', 'div[name^="message_id"]', 'div[name^="ids"]']
    selectors.forEach((sel) => {
      const $el = $(sel)
      if ($el.data("powertip")) {
        try { $el.powerTip("destroy") } catch { /* already destroyed */ }
      }
    })
  }

  #initCopyToClipboardElementValue() {
    $(document).on("click.chatHistory", ".copy_dialog_id", function () {
      const value = $(this).data("value")
      const dummyId = "dobj_" + value
      $("body").append(`<input id="${dummyId}" value="${value}" style="position:absolute;top:-1000px;">`)
      $(`#${dummyId}`).select()
      document.execCommand("copy")
      $(`#${dummyId}`).remove()
    })
  }

  #initHoverForDialogId() {
    const $links = $('a[name^="dialog_id"]')
    $links.off(".chatHistory").on({
      "powerTipPreRender.chatHistory": function () {
        $('a[name^="dialog_id"]').data("powertipjq", function () {
          const id = $(this).attr("name").replace("dialog_id_", "")
          return $(`<span class="copy_dialog_id" data-value="${id}">copy to clipboard</span>`)
        })
      },
      "powerTipOpen.chatHistory": () => {
        this.#initCopyToClipboardElementValue()
      },
    })

    $links.powerTip({ placement: "e", mouseOnToPopup: true })
  }

  #initHoverForMessage() {
    const $messages = $('div[name^="message_id"]')
    $messages.powerTip({ placement: "e", mouseOnToPopup: true })
    $messages.off(".chatHistory").on({
      "powerTipPreRender.chatHistory": function () {
        let result = ""
        let buf = ""
        const words = $(this).attr("val").split(" ")
        const maxSymbols = 150
        for (const word of words) {
          if ((buf + word).length > maxSymbols) {
            result = result + buf + "<br>"
            buf = word
          } else {
            buf = buf + " " + word
          }
        }
        $(this).data("powertip", htmlEscape(result.concat(buf)))
      },
    })
  }

  #initHoverForIds() {
    const $ids = $('div[name^="ids"]')
    $ids.powerTip({ placement: "n", mouseOnToPopup: true })
    $ids.off(".chatHistory").on({
      "powerTipPreRender.chatHistory": function () {
        let result = ""
        let buf = ""
        const words = $(this).attr("val").split(",")
        const maxSymbols = 150
        for (const word of words) {
          if ((buf + word).length > maxSymbols) {
            result = result + buf + "<br>"
            buf = word
          } else {
            buf = buf + "," + word
          }
        }
        $(this).data("powertip", result.concat(buf).replace(/,/, ""))
      },
    })
  }
}
